<template>
  <div class="shop-the-look critical-component-clear">
    <h2
      v-if="liquid.sectionHeading"
      class="text-body-l-medium shop-the-look__section-heading"
      v-text="liquid.sectionHeading"
    />
    <splide
      ref="splide"
      :options="splideOptions"
      :has-track="false"
    >
      <splide-track>
        <splide-slide
          v-for="(block, index) of liquid.blocks"
          :key="index"
          class="shop-the-look__slide"
          :class="{
            'shop-the-look__slide--flip': liquid.flipBlocks,
          }"
        >
          <heading-wrapper
            :heading="selectContent(block.heading, block.headingMobile)"
            :next-slide="nextSlide"
            :prev-slide="prevSlide"
            :total-looks="totalLooks"
            :current-look="currentLook"
            class="is-mobile"
          />
          <div
            v-if="block.image || block.imageMobile"
            class="shop-the-look__image"
          >
            <responsive-image
              :image="selectImageContent(block.image, block.imageMobile)"
              ratio="1:1"
              min-max="600-1200"
              :sizes="[
                `(min-width: ${breakpoints.l}) 50vw`,
                '100vw',
              ].join(', ')"
            />
          </div>

          <div class="shop-the-look__content">
            <heading-wrapper
              :heading="selectContent(block.heading, block.headingMobile)"
              :next-slide="nextSlide"
              :prev-slide="prevSlide"
              :total-looks="totalLooks"
              :current-look="currentLook"
              class="is-desktop"
            />

            <div
              v-if="block.description || block.descriptionMobile"
              class="shop-the-look__description"
              v-html="selectContent(block.description, block.descriptionMobile)"
            />

            <div
              v-if="block.products.length"
              class="shop-the-look__products"
            >
              <product-slides
                :products="block.products"
                :options="productsSplideOptions"
                block-name="shop-the-look"
              />
            </div>
          </div>
        </splide-slide>
        <splide-track />
      </splide-track>
    </splide>
  </div>
</template>

<script>

/**
 * Vue: shop-the-look (shop-the-look)
 * -----------------------------------------------------------------------------
 * Section for the PDP.
 *
 * @param {String} liquid.sectionId - Section's Liquid ID.
 * @param {Array} liquid.blocks - Section's blocks.
 *
 */
import { mapState } from 'vuex'
import { Splide, SplideSlide, SplideTrack } from '@splidejs/vue-splide'

import ResponsiveImage from '~global/images/responsive-image'

import ProductSlides from '~async/product-slides/product-slides'
import HeadingWrapper from '~async/shop-the-look/heading-wrapper'

import { values as breakpoints } from '~/config/breakpoints'

export default {
  name: 'ShopTheLook',
  components: {
    ResponsiveImage,
    Splide,
    SplideSlide,
    SplideTrack,
    ProductSlides,
    HeadingWrapper,
  },

  props: {
    liquid: {
      type: Object,
      default: () => ({
        sectionId: '',
        sectionHeading: '',
        flipBlocks: false,
        blocks: [],
      }),
    },
  },

  data() {
    return {
      splideOptions: {
        flickMaxPages: 1,
        focus: 'left',
        perMove: 1,
        perPage: 1,
        wheel: false,
        drag: false,
        flickPower: 200,
        pagination: false,
        type: 'fade',
        omitEnd: true,
        autoplay: false,
        respectReducedMotion: true,
      },
      productsSplideOptions: {
        perPage: 2,
        pagination: true,
        arrows: false,
        breakpoints: {
          [breakpoints.l]: {
            gap: 'var(--spacing-xl)',
            keyboard: true,
            perMove: 1,
            perPage: 2,
            arrows: true,
          },
        },
      },
      currentLook: 1,
      disableNext: false,
      disablePrev: true,
      breakpoints,
    }
  },

  computed: {

    /**
     * Map Vuex state.
     */
    ...mapState({
      isMobile: (state) => state.index.screen.mobile,
    }),

    /**
     * Compute total looks.
     * @returns {number}
     */
    totalLooks() {
      return this.liquid.blocks.length
    },
  },

  methods: {

    /**
     * Go to next slide.
     */
    nextSlide() {
      if (this.currentLook < this.totalLooks) {
        this.$refs.splide.go('>')
        this.currentLook++
        this.disablePrev = false
      }
      if (this.currentLook === this.totalLooks) {
        this.disableNext = true
      }
    },

    /**
     * Go to previous slide.
     */
    prevSlide() {
      if (this.currentLook > 1) {
        this.$refs.splide.go('<')
        this.currentLook--
        this.disableNext = false
      }
      if (this.currentLook === 1) {
        this.disablePrev = true
      }
    },

    /**
     * Selects mobile or desktop image.
     * @param {Object} desktopImage - The desktop version of the image.
     * @param {Object} mobileImage - The mobile version of the image.
     * @returns {String} - The selected content based on the device type.
     */
    selectImageContent(desktopImage, mobileImage) {
      if (this.isMobile) {
        return mobileImage.src ? mobileImage : desktopImage
      }
      return desktopImage.src ? desktopImage : mobileImage
    },

    /**
     * Selects mobile or desktop content.
     * @param {String} desktopContent - The desktop version of the content.
     * @param {String} mobileContent - The mobile version of the content.
     * @returns {String} - The selected content based on the device type.
     */
    selectContent(desktopContent, mobileContent) {
      if (this.isMobile) {
        return mobileContent || desktopContent
      }
      return desktopContent || mobileContent
    },
  },
}
</script>

<style lang="scss">
@import '@/components/splide';
@import './shop-the-look';
</style>
